import React from "react";
import _ from "lodash";
import moment from "moment";
import getDayRangeString from "../../../utils/day-range-string";
import Chart from "../../../parts/chart";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import MetricsBackButton from "./metrics-back-button";
import set from "@rill/set";
import { getChartLabels } from "../../../utils/chart-labels";

import { PERIOD_FORMATS, DATE_FORMATS } from "../../../constants";
import SvgIcon from "../../../parts/SvgIcon";
import NavigationComponent from "../calendar/components/NavigationComponent";
import { ReportsBackHandler } from "../calendar/components/ReportsBackHandler";
import { AthleteLinks } from "../../../parts/athlete-links";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { useTranslation } from "react-i18next";
import { isAdminOrOwner } from "../../../utils/organization-permissions-helpers";

const { DATE_PICKER_FORMAT } = DATE_FORMATS;

const View = (props) => {
  const { locals } = useRillContext();
  const { t } = useTranslation("athlete-portal");

  const {
    exercisesVolume,
    metricsIntensity,
    query,
    pathname,
    search,
    athlete,
  } = locals;

  const { period, units } = query;
  const organizationCoach = query.organizationCoach || undefined;

  const period_short = PERIOD_FORMATS[period];
  const selectedMetricsVolume =
    query.type === "total"
      ? exercisesVolume
      : _.pick(exercisesVolume, [query.type]);
  const keys = _(selectedMetricsVolume)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = getChartLabels(keys, query, period);

  const selectedMetricsVolumeGroups = _.values(selectedMetricsVolume);
  const totalMetricVolumes = _.map(keys, (key) =>
    _.sumBy(selectedMetricsVolumeGroups, `${key}.volume`)
  );
  const totalVolumes = _.map(totalMetricVolumes, (volume) =>
    locals.fromMetric(volume, units)
  );
  const volumeSeries = [
    {
      name: query.type,
      className: query.type,
      data: totalVolumes,
    },
  ];

  const intensitySeries = _.chain(keys)
    .map((key, i) => {
      const total = totalMetricVolumes[i];
      const metrics = _.compact(_.map(selectedMetricsVolumeGroups, key));

      const average = _.reduce(
        metrics,
        (prev, metric) => prev + metric.average * (metric.volume / total),
        0
      );
      const peak = _.max(_.map(metrics, "peak")) || 0;

      return [
        { x: key, y: average },
        { x: key, y: peak },
      ];
    })
    .unzip()
    .value();

  const intensitySeriesValues = _.map(_.flatten(intensitySeries), "y");
  const intensitySeriesMax = Math.max(
    Math.round((_.max(intensitySeriesValues) * 100) / 10) * 10,
    100
  );
  const intensitySeriesMin = Math.min(
    Math.max(Math.round((_.min(intensitySeriesValues) * 100) / 10) * 10, 0),
    40
  );

  const handleDayChange = (date, type) => {
    // Creating the new query params (type=date) e.g.: start=June 21, 2024
    const searchString = `${encodeURIComponent(type)}=${encodeURIComponent(
      date
    )}`;

    // Creating a new <a> element to navigate to the new URL throught it
    // (tried with submit-form and didn't work)
    var linkElement = document.createElement("a");

    // Setting its href attribute to the new URL
    linkElement.href = `/actions/refresh?${searchString}`;

    // Appending it to the body and simulating the click
    document.body.appendChild(linkElement);
    linkElement.click();
    document.body.removeChild(linkElement);
  };

  return (
    <main role="reports-wrapper" className="Main Metrics">
      <NavigationComponent
        query={query}
        pathName={"/athlete-portal/metrics"}
        textKey="nav.calendarDayView"
        classNames="mobile-hidden"
      />
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athlete}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(athlete?._id)})`,
            }}
          />
          <AthleteSelect locals={locals} />
        </div>
      </div>
      <article role="report-content" id="strength-metrics">
        <ReportsBackHandler
          title={t("metrics.strengthMetrics")}
          locals={locals}
          customBackUrl={`/athlete-portal/metrics/${search}`}
        />
        <header>
          <hgroup className="metrics-title">
            <MetricsBackButton
              url={`/athlete-portal/metrics/${search}`}
              classNames="mobile-hidden"
            />
            <SvgIcon
              src="/img/new-icons/volume-intensity-ico.svg"
              className="metrics-icon mobile-hidden"
            />
            <div>
              <SvgIcon
                src="/img/new-icons/volume-intensity-ico.svg"
                className="metrics-icon only-show-mobile"
              />
              <h3>{t("metrics.volumeIntensity")}</h3>
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("metricsDrawer.kg"), value: "metric" },
                  { text: t("metricsDrawer.lbs"), value: "imperial" },
                ]}
                className={"only-show-mobile"}
              />
              <p className="p4 mobile-hidden">
                {t("volumeIntensity.description")}
              </p>
            </div>
            <p className="p4 only-show-mobile">
              {t("volumeIntensity.description")}
            </p>
          </hgroup>
          <hgroup className="metrics-dates">
            <div role="calendar-date-selectors">
              <DateInput
                name="start"
                placeholder={t("reports.startDate")}
                value={query.start}
                max={query.end}
                onChange={(date) => handleDayChange(date, "start")}
              />
              <span>{t("reports.to")}</span>
              <DateInput
                name="end"
                placeholder={t("reports.endDate")}
                value={query.end}
                min={query.start}
                onChange={(date) => handleDayChange(date, "end")}
                alignRight={true}
              />
            </div>
            <div>
              <ToggleLinksInput
                name="period"
                options={[
                  { text: t("calendar.month"), value: "month" },
                  { text: t("calendar.week"), value: "week" },
                  { text: t("calendar.day"), value: "day" },
                ]}
                activePrimary={true}
              />
            </div>
          </hgroup>
          <hgroup className="metrics-data-options">
            <ToggleLinksInput
              name="units"
              options={[
                { text: t("metricsDrawer.kg"), value: "metric" },
                { text: t("metricsDrawer.lbs"), value: "imperial" },
              ]}
              className={"mobile-hidden"}
            />

            <ToggleLinksInput
              className="tabs"
              name="type"
              options={[
                { text: t("strengthMetricsShared.total"), value: "total" },
                { text: t("strengthMetricsShared.squat"), value: "squat" },
                { text: t("strengthMetricsShared.bench"), value: "bench" },
                { text: t("strengthMetricsShared.deadlift"), value: "deadlift" },
              ]}
            />
          </hgroup>
        </header>

        <div className="Chart-Frame bar-chart">
          <div className="body">
            {!_.isEmpty(selectedMetricsVolume) && (
              <div>
                <div className="legend">
                  <p className={` p4 bar series ${query.type}`}>{t("strengthMetricsShared.volumeAxisLabel")}</p>
                  <p className="p4 line series-a">{t("volumeIntensity.legendAvgIntensity")}</p>
                  <p className="p4 line series-b">{t("volumeIntensity.legendPeakIntensity")}</p>
                </div>
                <div className="axis-labels">
                  <div className="left">{t("strengthMetricsShared.volumeAxisLabel")}</div>
                  <div className="right">{t("strengthMetricsShared.intensityAxisLabel")}</div>
                </div>
                <div className="chart-fullsize">
                  <div className="labels">
                    <Chart
                      type="Bar"
                      only="label"
                      data={{ series: volumeSeries }}
                      options={{
                        width: "60px",
                        height: "380px",
                        stackBars: true,
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          showGrid: false,
                          showLabel: true,
                          onlyInteger: true,
                          scaleMinSpace: 30,
                        },
                      }}
                    />

                    <Chart
                      type="Bar"
                      only="label"
                      data={{ series: [] }}
                      options={{
                        width: "60px",
                        height: "380px",
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          type: process.browser && require("chartist").StepAxis,
                          stretch: true,
                          showGrid: false,
                          showLabel: true,
                          ticks: _.range(
                            intensitySeriesMin,
                            intensitySeriesMax + 10,
                            10
                          ).map((i) => `${i}%`),
                        },
                      }}
                    />
                  </div>

                  <div className="data">
                    <Chart
                      type="Bar"
                      data={{
                        labels: labels,
                        series: volumeSeries,
                      }}
                      options={{
                        width: `${_.size(keys) * 40}px`,
                        height: "380px",
                        stackBars: true,
                        chartPadding: { left: 35, right: 75 },
                        axisX: { showGrid: false, showLabel: true },
                        axisY: {
                          showGrid: true,
                          showLabel: false,
                          scaleMinSpace: 30,
                        },
                      }}
                      tooltip={(item, i) => {
                        const key = keys[i];
                        const date = moment
                          .utc(query.start, DATE_PICKER_FORMAT)
                          .add(key, period);
                        const dayRange = getDayRangeString(
                          [
                            date,
                            date.clone().add(1, period).subtract(1, "day"),
                          ],
                          query
                        );
                        const avgIntensity = _.get(
                          _.find(intensitySeries[0], { x: key }),
                          "y",
                          0
                        );
                        const peakIntensity = _.get(
                          _.find(intensitySeries[1], { x: key }),
                          "y",
                          0
                        );
                        const totalVolume = totalVolumes[i];
                        return (
                          <div className="tooltip" style={{ left: item.x }}>
                            <div className="details">
                              <header>{dayRange}</header>
                              <div>
                                {t("volumeIntensity.tooltipVolume")}{" "}
                                <b>{locals.withWeight(totalVolume, units)}</b>
                              </div>
                              <div>
                                {t("volumeIntensity.tooltipAvgIntensity")}{" "}
                                <b>{_.round(avgIntensity * 100)}%</b>
                              </div>
                              <div>
                                {t("volumeIntensity.tooltipPeakIntensity")}{" "}
                                <b>{_.round(peakIntensity * 100)}%</b>
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    />

                    {_.size(keys) >= 2 && (
                      <Chart
                        type="Line"
                        data={{ series: intensitySeries }}
                        options={{
                          width: `${_.size(keys) * 40}px`,
                          height: "380px",
                          showPoint: false,
                          chartPadding: { left: 35, right: 75 },
                          axisX: { showGrid: false, showLabel: false },
                          axisY: {
                            low: intensitySeriesMin / 100,
                            high: intensitySeriesMax / 100,
                            showGrid: false,
                            showLabel: false,
                          },
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </main>
  );
};

const VolumeIntensity = (app) => {
  app.get(
    "/volume-intensity",
    set("heading", "Volume & Intensity"),
    async ({ req, res, load, locals, api, fail }) => {
      const isAllowToViewAllOrganizationsAthletes = isAdminOrOwner(
        locals.organizationRoleName
      );

      if (
        locals.activeUser.organizationId &&
        isAllowToViewAllOrganizationsAthletes &&
        !locals.allAthletesFromOrganization
      ) {
        await load("allAthletesFromOrganization");
      }

      const { query } = req;
      _.defaults(req.query, {
        type: "total",
        period: "day",
        units: locals.athlete.units,
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await Promise.all([
        load("exercisesVolume", {
          start: moment
            .utc(query.start, DATE_PICKER_FORMAT)
            .startOf("day")
            .toDate(),
          end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
          period: query.period,
          proxyAthlete: query.athlete,
          reqSource: "metrics",
        }),
      ]);

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        // await load('profileUser', { user: locals.athlete._id });
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  user: {
                    name: locals.athlete.name,
                    registered: locals.athlete.registered,
                    avatar: locals.athlete.avatar,
                    units: locals.athlete.units,
                  },
                  type: "volume-intensity",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick(["exercisesVolume"])
                      .assign({
                        period: query.period,
                        start: query.start,
                        end: query.end,
                      })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);
          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }

      locals.modal = {
        share: <ShareModal />,
        "activity-feed": <ActivityDrawer />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default VolumeIntensity;
